import { HttpClient, type Method } from '@wix/http-client';

/**
 * The Wix http client used under the `symphonyInstanceAuthWeb` gate. Every data
 * call on the instance-auth path goes through this — no raw `fetch` under the
 * gate (docs/AUTH-MIDDLEWARE-AUDIT.md §4.1).
 *
 * `@wix/http-client` is axios-style (returns `{ data, status }`, throws on
 * non-2xx by default), so this adapts it back to a `Response` and mirrors
 * `fetch` semantics — a non-2xx resolves to a non-ok `Response` (via
 * `validateStatus`), a network failure rejects — so the call sites keep the
 * fetch contract (`res.ok`, `await res.json()`).
 */
const client = new HttpClient();

// Statuses that must not carry a body when constructing a Response.
const NULL_BODY_STATUSES = new Set([101, 204, 205, 304]);

function toHeaders(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string') out[k] = v;
  }
  return out;
}

export async function instanceHttpFetch(input: string, init: RequestInit = {}, instance?: string): Promise<Response> {
  const headers: Record<string, string> = { ...(init.headers as Record<string, string> | undefined) };
  if (instance) headers.Authorization = instance;

  const resp = await client.request<string>({
    url: input,
    method: (init.method ?? 'GET') as Method,
    data: init.body ?? undefined,
    headers,
    withCredentials: true,
    // Keep the raw body string so call sites can `.json()`/`.text()` it themselves.
    responseType: 'text',
    transformResponse: (d: unknown) => d,
    // Mirror fetch: resolve on any status; the caller inspects `res.ok`.
    validateStatus: () => true,
  });

  const body = NULL_BODY_STATUSES.has(resp.status) ? null : ((resp.data as unknown as string) ?? null);
  return new Response(body, { status: resp.status, statusText: resp.statusText, headers: toHeaders(resp.headers) });
}
